import { useCallback, useMemo } from "react";
import { useDropdown } from "./use-dropdown";
import { useActionSheet } from "@expo/react-native-action-sheet";
import { useI18n } from "./i18n-hooks";
import { useSnackbar } from "./snackbar";
import { useIsScreenSizeMin } from "./useIsScreenSizeMin";
import { DropdownItemsType, useItemsDropdown } from "../components/Dropdown";
import { useTheme } from "./use-theme";
import { GestureResponderEvent } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

export type UseActionSheetOrDropdownProps = {
    items: DropdownItemsType[];
    withScreenActionsPadding?: boolean;
    position?: "left" | "right";
    keepOpen?: boolean;
    onBlur?: () => void;
    uniqueSelection?: boolean;
    setIsLoading?: (isLoading: boolean) => void;
    destructiveButtonIndex?: number;
};

export const useActionSheetOrDropdown = ({
    items,
    setIsLoading,
    destructiveButtonIndex,
    withScreenActionsPadding,
    position,
    keepOpen,
    onBlur,
    uniqueSelection,
}: UseActionSheetOrDropdownProps) => {
    const { showActionSheetWithOptions } = useActionSheet();
    const { showSnackbar } = useSnackbar();
    const isLarge = useIsScreenSizeMin("large");
    const i18n = useI18n();
    const insets = useSafeAreaInsets();
    const { colors } = useTheme();

    const itemsWithCancel = useMemo<DropdownItemsType[]>(() => {
        const _ret = [
            ...items.map((i, index) => ({
                ...i,
                ...(index === destructiveButtonIndex
                    ? {
                          color: colors.error,
                      }
                    : {}),
                ...(isLarge
                    ? {
                          onClick: async () => {
                              setIsLoading?.(true);
                              await i.onClick();
                              setIsLoading?.(false);
                          },
                      }
                    : {}),
            })),
        ];
        if (!isLarge) {
            _ret.push({
                label: i18n.t("cancel"),
                onClick: () => {},
            });
        }
        return _ret;
    }, [colors.error, destructiveButtonIndex, i18n, isLarge, items, setIsLoading]);

    const dropdownComponent = useItemsDropdown({
        items: itemsWithCancel,
        uniqueSelection,
    });

    const dropdownProps = useDropdown({
        component: dropdownComponent,
        withScreenActionsPadding,
        position,
        keepOpen,
        onBlur,
        uniqueSelection,
    });

    const onSelectOption = useCallback(
        async (selectedIndex = 0) => {
            try {
                await itemsWithCancel[selectedIndex].onClick();
            } catch (e) {
                console.warn(e);
                showSnackbar({
                    text: i18n.t("documents.select_error"),
                    mode: "error",
                });
            }

            setIsLoading?.(false);
        },
        [itemsWithCancel, i18n, setIsLoading, showSnackbar],
    );

    const openActionSheet = useCallback(
        (_e?: GestureResponderEvent) => {
            setIsLoading?.(true);
            try {
                showActionSheetWithOptions(
                    {
                        options: itemsWithCancel.map((i) => i.label),
                        cancelButtonIndex: items.length,
                        destructiveButtonIndex,
                        containerStyle: {
                            paddingBottom: insets.bottom,
                        },
                    },
                    async (selectedIndex = 0) => {
                        await onSelectOption(selectedIndex);
                    },
                );
            } finally {
                /* empty */
            }
        },
        [
            setIsLoading,
            showActionSheetWithOptions,
            itemsWithCancel,
            items.length,
            destructiveButtonIndex,
            insets.bottom,
            onSelectOption,
        ],
    );

    return { ref: dropdownProps.ref, onPress: isLarge ? dropdownProps.onPress : openActionSheet };
};
